// src/components/PassiveSearch/PassiveSearchContainer.jsx

import React, { useState, useCallback, useEffect } from 'react';
import {
    Typography,
    Box as MuiBox,
    Alert,
    Fade,
    useTheme,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TablePagination
} from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CloseIcon from '@mui/icons-material/Close';

import useAuth from '../../hooks/useAuth';
import PassiveSearchView from './PassiveSearchView';
import CandidateResultsTable from './ResultsTable/CandidateResultsTable';
import { useCandidateSearch } from './hooks/useCandidateSearch';
import { getScoreChipColor, getScoreColorForText } from './utils/candidateHelpers';
import { headCells, rowsPerPageOptionsConfig } from './config/passiveSearchTableConfig';
import HowSearchWorksDialogContent from './ResultsTable/HowSearchWorksDialogContent';
import SpinningCustomLogo from '../Common/SpinningCustomLogo';
import { useTour } from '../../shared/TourContext';
import { passiveSearchTourSteps } from '../../tours/tourSteps';
import ShortlistSidebar from './components/ShortlistSidebar';
import { CompareDialog } from './components/SearchModals';
import PotentialCandidateView from '../PotentialCandidateView/PotentialCandidateView';
import PassiveSearchEmptyState from './components/PassiveSearchEmptyState'; // <-- IMPORT NEW COMPONENT

const GLOBAL_HEADER_HEIGHT = theme => theme.spacing(8);


const PassiveSearchContainer = () => {
    const theme = useTheme();
    const { isAuth: firebaseUser } = useAuth();
    const { setSteps } = useTour();

    const {
        jobDescription,
        handleJobDescriptionChange,
        handleSubmitSearch,
        sortedCandidates,
        paginatedCandidates,
        isProcessing,
        error,
        infoMessage,
        sortConfig,
        handleSortRequest,
        page,
        rowsPerPage,
        handleChangePage,
        handleChangeRowsPerPage,
        searchHistory,
    } = useCandidateSearch(firebaseUser);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogContentElement, setDialogContentElement] = useState(null);
    const [shortlist, setShortlist] = useState([]);
    const [compareDialogOpen, setCompareDialogOpen] = useState(false);
    const [quickViewCandidate, setQuickViewCandidate] = useState(null);


    useEffect(() => {
        setSteps(passiveSearchTourSteps);
        return () => setSteps([]);
    }, [setSteps]);

    const handleShortlistToggle = (candidateUuid) => {
        setShortlist((prevShortlist) => {
            const isShortlisted = prevShortlist.some((c) => c.uuid === candidateUuid);
            if (isShortlisted) {
                return prevShortlist.filter((c) => c.uuid !== candidateUuid);
            } else {
                const candidateToAdd = sortedCandidates.find((c) => c.uuid === candidateUuid);
                if (candidateToAdd) {
                    return [...prevShortlist, candidateToAdd];
                }
                return prevShortlist;
            }
        });
    };

    const handleClearShortlist = () => {
        setShortlist([]);
    };

    const handleOpenCompareDialog = () => {
        setCompareDialogOpen(true);
    };

    const handleExportShortlist = () => {
        console.log('Exporting candidates:', shortlist);
        alert(`Exporting ${shortlist.length} candidates.`);
    };


    const handleOpenDialog = (title, contentElement) => {
        setDialogTitle(title);
        setDialogContentElement(contentElement);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleHowSearchWorksClick = () => {
        handleOpenDialog("How Our Search Works", <HowSearchWorksDialogContent />);
    };
    
    const handleCandidateQuickView = (candidate) => {
        setQuickViewCandidate(candidate);
    };

    const hasContentToDisplayBelowSearch = sortedCandidates.length > 0 || !!error || !!infoMessage;
    const shouldCenterPageContent = !isProcessing && !hasContentToDisplayBelowSearch;
    const shouldCenterSearchAndSpinner = isProcessing && !hasContentToDisplayBelowSearch;

    // --- NEW LOGIC FOR EMPTY STATE ---
    const showEmptyState = !isProcessing && (!!error || !!infoMessage || (jobDescription && sortedCandidates.length === 0));

    return (
        <MuiBox
            sx={{
                display: 'flex',
                flexDirection: 'row',
                height: 'calc(100vh - 64px)', // Adjust for header height
                width: '100%',
                bgcolor: 'background.default',
            }}
        >
            <MuiBox
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexGrow: 1,
                    overflowY: 'auto',
                }}
            >
                <MuiBox
                    sx={{
                        width: '100%',
                        maxWidth: '1280px',
                        px: { xs: 2, sm: 3, md: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: { xs: 2, sm: 2.5, md: 3 },
                        pt: (shouldCenterPageContent || shouldCenterSearchAndSpinner) ? 0 : GLOBAL_HEADER_HEIGHT(theme),
                        pb: { xs: 4, sm: 6 },
                        flexGrow: 1,
                        justifyContent: shouldCenterPageContent || shouldCenterSearchAndSpinner ? 'center' : 'flex-start',
                    }}
                >
                    <MuiBox
                        sx={{
                            width: '100%',
                            maxWidth: '600px',
                            mb: (shouldCenterPageContent && !isProcessing) ? 3.5 : 2,
                            display: 'flex',
                            flexDirection: 'column',
                            pt: 3
                        }}
                    >
                        <PassiveSearchView
                            jobDescription={jobDescription}
                            onJobDescriptionChange={handleJobDescriptionChange}
                            onSubmit={handleSubmitSearch}
                            isProcessing={isProcessing}
                            error={null}
                            onHowSearchWorksClick={handleHowSearchWorksClick}
                            searchHistory={searchHistory}
                        />
                    </MuiBox>

                    {isProcessing && (
                        <Fade in={isProcessing} timeout={300}>
                            <MuiBox sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 4 }}>
                                <SpinningCustomLogo size={64} />
                                <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                                    Analyzing description and finding top candidates...
                                </Typography>
                            </MuiBox>
                        </Fade>
                    )}

                    {/* --- UPDATED TO RENDER NEW EMPTY STATE COMPONENT --- */}
                    {showEmptyState && (
                        <MuiBox sx={{ width: '100%', maxWidth: '700px' }}>
                            <PassiveSearchEmptyState error={error} info={infoMessage} />
                        </MuiBox>
                    )}

                    {!isProcessing && sortedCandidates.length > 0 && (
                        <MuiBox sx={{ width: '100%' }}>
                            <CandidateResultsTable
                                sortedCandidates={paginatedCandidates}
                                sortConfig={sortConfig}
                                handleSortRequest={handleSortRequest}
                                headCells={headCells}
                                getScoreChipColor={getScoreChipColor}
                                getScoreColorForText={(score) => getScoreColorForText(score, theme)}
                                shortlist={shortlist}
                                onShortlistToggle={handleShortlistToggle}
                                onCandidateClick={handleCandidateQuickView}
                            />
                            <TablePagination
                                component="div"
                                count={sortedCandidates.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={rowsPerPageOptionsConfig}
                                labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`}
                                sx={{ mt: 0, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}
                            />
                        </MuiBox>
                    )}
                </MuiBox>
            </MuiBox>
            
            <ShortlistSidebar
                shortlist={shortlist}
                onClose={handleClearShortlist}
                onToggle={handleShortlistToggle}
                onCompare={handleOpenCompareDialog}
                onExport={handleExportShortlist}
            />

            <CompareDialog
                open={compareDialogOpen}
                onClose={() => setCompareDialogOpen(false)}
                candidates={shortlist}
            />

            <Dialog open={!!quickViewCandidate} onClose={() => setQuickViewCandidate(null)} maxWidth="lg" fullWidth>
                <DialogContent sx={{ p: 0 }}>
                    <PotentialCandidateView
                        idealCandidate={{}}
                        potentialCandidate={quickViewCandidate}
                        onClose={() => setQuickViewCandidate(null)}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={openDialog} onClose={handleCloseDialog} PaperProps={{ sx: { borderRadius: '12px', minWidth: '300px', maxWidth: '600px', bgcolor: 'background.paper' } }}>
                <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {dialogTitle}
                    <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ color: 'text.secondary' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ pt: 2, typography: 'body2', color: 'text.secondary' }}>
                    {dialogContentElement}
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button onClick={handleCloseDialog} variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: '8px' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </MuiBox>
    );
};

export default PassiveSearchContainer;